import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { usePartnerDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { PartnerGMetrixTable } from 'components/PartnerGMetrixTable/PartnerGMetrixTable';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { Partner, PartnerApi } from 'apis/PartnerApi';
import { Col, Container, Row } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { ListItemCount } from 'types';
import { useIntl } from 'react-intl';

import 'components/PartnerGMetrixTableDialog/partner-gmetrix-table-dialog.scss';

type Option = 'Change' | undefined;

export interface PartnerGMetrixTableDialogShowOptions {
    customSuccessButtonText?: string;
    dialogCustomTitle?: string;
    multiselect?: boolean;
    option?: Option;
    scopeId?: number;
}

export interface PartnerGMetrixTableDialogResult {
    ids: number[];
    partners: Partner[];
}

export interface PartnerGMetrixTableDialogHandle {

    hide: () => void;

    show: (overrides?: PartnerGMetrixTableDialogShowOptions) => Promise<PartnerGMetrixTableDialogResult | null>;
}

interface PartnerGMetrixTableDialogProps {
    customSuccessButtonText?: string;
    dialogCustomTitle?: string;
    multiselect?: boolean;
    option?: Option;
    scopeId?: number;
    setPartnerKendoCount?: React.Dispatch<React.SetStateAction<ListItemCount>>;
    showCancelButtonInDialog?: boolean;
}

export const PartnerGMetrixTableDialog = forwardRef<PartnerGMetrixTableDialogHandle, PartnerGMetrixTableDialogProps>((props, forwardedRef) => {

    const {
        customSuccessButtonText: propCustomSuccessButtonText,
        dialogCustomTitle: propDialogCustomTitle,
        multiselect: propMultiselect = false,
        option: propOption = 'Change',
        scopeId: propScopeId,
        setPartnerKendoCount,
        showCancelButtonInDialog = true
    } = props;

    const [state, setState] = useSuperState({
        loadingGridData: true,
        overrides: null as PartnerGMetrixTableDialogShowOptions | null,
        partners: [] as Partner[],
        searchTerm: '',
        selectedPartners: [] as Partner[]
    });
    const { loadingGridData, overrides, partners, searchTerm, selectedPartners } = state;
    const isOpen = overrides !== null;
    const { formatMessage } = useIntl();
    const partnerDefaultLookupSearchFields = usePartnerDefaultLookupSearchFields();

    const resolverRef = useRef<((value: PartnerGMetrixTableDialogResult | null) => void) | null>(null);

    const resolvedScopeId = overrides?.scopeId ?? propScopeId;
    const resolvedMultiselect = overrides?.multiselect ?? propMultiselect;
    const resolvedOption = overrides?.option ?? propOption;
    const resolvedTitle = overrides?.dialogCustomTitle ?? propDialogCustomTitle;
    const resolvedSuccessText = overrides?.customSuccessButtonText ?? propCustomSuccessButtonText;

    /** Settle the promise, reset state, and close the dialog. */
    const settle = useCallback((value: PartnerGMetrixTableDialogResult | null) => {

        const resolve = resolverRef.current;
        resolverRef.current = null;
        setState({
            loadingGridData: true,
            overrides: null,
            partners: [],
            searchTerm: '',
            selectedPartners: []
        });
        resolve?.(value);

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(forwardedRef, () => ({
        hide: () => settle(null),
        show: (nextOverrides?: PartnerGMetrixTableDialogShowOptions) => new Promise<PartnerGMetrixTableDialogResult | null>(resolve => {

            // Cancel any in-flight pick.
            resolverRef.current?.(null);
            resolverRef.current = resolve;
            setState({ overrides: nextOverrides ?? {} });

        })
    }), [settle]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve pending promise on unmount.
    useEffect(() => () => {

        resolverRef.current?.(null);
        resolverRef.current = null;

    }, []);

    // Load grid data when dialog opens or scopeId changes.
    useEffect(() => {

        if (!isOpen) {

            setState({ loadingGridData: false, partners: [] });
            return;

        }

        if (!resolvedScopeId) {

            setState({ loadingGridData: false });
            return;

        }

        setState({ loadingGridData: true });

        let cancelled = false;
        PartnerApi.GetAll(resolvedScopeId).then(partnerListInTable => {

            if (cancelled) return;

            partnerListInTable.forEach(partner => (partner.Id = partner.ID));

            if (resolvedOption === 'Change') {

                setState({ partners: partnerListInTable });
                if (setPartnerKendoCount && partnerListInTable.length > 0) {

                    setPartnerKendoCount({
                        count: partnerListInTable.length,
                        firstId: partnerListInTable[0].ID
                    });

                }

            }

            setState({ loadingGridData: false });

        });

        return () => {

            cancelled = true;

        };

    }, [isOpen, resolvedScopeId, resolvedOption, setPartnerKendoCount]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleConfirm = () => {

        settle({
            ids: selectedPartners.map(partner => partner.Id),
            partners
        });

    };

    return (
        <GMetrixDialog
            actionButtonText={
                resolvedSuccessText
                    ? resolvedSuccessText
                    : `${resolvedOption} ${selectedPartners.length > 0 ? selectedPartners.length : ''} ${formatMessage({ id: 'partners.84d264f4', defaultMessage: 'Partners' })}`
            }
            dialogLabel={(
                <div className="d-flex align-items-center">
                    <div>
                        {resolvedTitle ? resolvedTitle : `${resolvedOption} ${formatMessage({ id: 'partners.84d264f4', defaultMessage: 'Partners' })}`}
                    </div>
                </div>
            )}
            actionButtonClick={handleConfirm}
            actionButtonDisabled={selectedPartners.length === 0}
            onCancel={() => settle(null)}
            show={isOpen}
            showCancelButton={showCancelButtonInDialog}
        >
            <Container fluid className="partner-gmetrix-table-dialog">
                <Row>
                    <Col className="pb-3">
                        <PartnerGMetrixTable
                            selectableRows
                            advancedSearchFields={partnerDefaultLookupSearchFields}
                            allowExport={false}
                            columnData={[{ field: 'Id' }, { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) }]}
                            loadingGridData={loadingGridData}
                            multiselect={resolvedMultiselect}
                            onSelectedChange={partners => setState({ selectedPartners: partners })}
                            partners={partners.filter(partner => partner.Name?.toLowerCase().includes(searchTerm.toLowerCase()))}
                            searchTerm={searchTerm}
                            selectedPartners={selectedPartners}
                            setSearchTerm={term => setState({ searchTerm: term })}
                            toggleColumns={false}
                        />
                    </Col>
                </Row>
            </Container>
        </GMetrixDialog>
    );

});

PartnerGMetrixTableDialog.displayName = 'PartnerGMetrixTableDialog';
